import React, { useState, useMemo } from "react";
import { useDateFilteredLeads } from "@/hooks/useDateFilteredData";
import { useUsers } from "@/hooks";
import { getCumulativeStatuses } from "@/constants/globalConstants";
import { useLeadFilters } from "../../verve-leads/leads/hooks/useLeadFilters";
import { InlineLoader as Loader, Tabs } from "@/components/ui";
import ExpenseApprovalTab from "../components/ExpenseApprovalTab";
import MaterialApprovalTab from "../components/MaterialApprovalTab";
import AppliancesTab from "../components/appliances_tab";
import BudgetApprovalsTab from "../components/budget_approvals_tab";
import AdditionalApprovalsTab from "../components/AdditionalApprovalsTab";
import SaleApprovalTab from "../components/SaleApprovalTab";
import ApprovalsStatsCards from "../components/ApprovalsStatsCards";
import { ClipboardCheck, Receipt, Package, CheckSquare, IndianRupee, FileQuestion } from "lucide-react";

export default function ApprovalsScreen() {
  const [activeTab, setActiveTab] = useState("expenses");

  const {
    debouncedSearch,
    filters,
    page,
    sortBy
  } = useLeadFilters();

  const queryParams = useMemo(() => ({
    search: debouncedSearch,
    page,
    limit: 100, // Fetch up to 100 pending approvals for now
    status: getCumulativeStatuses('customer_acceptance'),
    sort: sortBy
  }), [debouncedSearch, page, sortBy]);

  // Force the filter status to 'customer_acceptance' so it only fetches those leads
  const { data: leadsData, isLoading: leadsLoading, refetch } = useDateFilteredLeads(queryParams, { ...filters, status: 'customer_acceptance' });
  const { teamMembers } = useUsers({ limit: 1000, includeOwner: true });

  const rawLeads = Array.isArray(leadsData) ? leadsData : (leadsData?.leads || leadsData?.projects || []);
  const leads = rawLeads.map(l => ({ ...l, isLocked: false }));

  const tabs = [
    // { id: "quotation", label: "Initial Quotation Approval", icon: ClipboardCheck },
    { id: "expenses", label: "Expense Approval", icon: Receipt },
    { id: "materials", label: "Material Approval", icon: Package },
    { id: "appliances", label: "Ordering Approvals", icon: CheckSquare },
    { id: "sales", label: "Sale Approvals", icon: CheckSquare },
    { id: "budget", label: "Budget Approvals", icon: IndianRupee },
    { id: "additional", label: "Additional Approvals", icon: FileQuestion }
  ];

  return (
    <div className="flex flex-col gap-6 w-full max-w-full pb-2 overflow-x-hidden">
      <div className="relative overflow-hidden w-full h-32 bg-white border border-slate-200 rounded-xl flex items-center justify-between shadow-sm">
        <div className="pl-8 z-20">
          <h1 className="font-serif font-medium text-3xl text-slate-900 tracking-wide">
            Approvals Management
          </h1>
          <p className="text-sm font-body text-slate-400 mt-2 font-medium">Verve Kitchens Approvals Management</p>
        </div>
        <div className="w-[50%] h-full relative overflow-hidden flex-shrink-0">
          <div className="absolute inset-0 bg-gradient-to-r from-white via-white/70 to-transparent z-10 w-48" />
          <img
            src="/kitchens/1.jpg"
            alt="Approvals Management"
            className="absolute top-0 right-0 w-full h-full object-cover object-center opacity-90"
          />
        </div>
      </div>

      <ApprovalsStatsCards />

      <div className="flex flex-col gap-2">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200/60 p-2">
          <Tabs 
            tabs={tabs}
            activeTab={activeTab}
            onChange={setActiveTab}
            variant="sub"
          />
        </div>

        <div className="flex flex-col">
          {/* {activeTab === "quotation" && (
            leadsLoading ? (
              <div className="flex justify-center items-center h-40">
                <Loader size="lg" />
              </div>
            ) : (
              <InitialQuotationApproval 
                leads={leads} 
                isLoading={leadsLoading} 
                onRefresh={refetch} 
                teamMembers={teamMembers || []} 
                isViewOnly={false} 
              />
            )
          )} */}
          {activeTab === "expenses" && (
            <ExpenseApprovalTab />
          )}
          {activeTab === "materials" && (
            <MaterialApprovalTab />
          )}
          {activeTab === "appliances" && (
            <AppliancesTab />
          )}
          {activeTab === "budget" && (
            <BudgetApprovalsTab />
          )}
          {activeTab === "sales" && (
            <SaleApprovalTab />
          )}
          {activeTab === "additional" && (
            <AdditionalApprovalsTab />
          )}
        </div>
      </div>

    </div>
  );
}